import React, { useMemo } from 'react'
import { Combobox, InputField } from '@enamelai/design-system'
import ChartNameComboBox from '@components/pages/Charting/ChartNameComboBox.component'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import type { RootState } from '@store/index'
import { shallowEqual, useStore } from 'react-redux'
import {
  setCreateDentalChart,
  setDentalUpdated,
} from '@store/reducers/charting.reducer'
import {
  CHART_PROVIDER_INVALID_ERROR,
  isChartProviderHardError,
} from '@utils/chart-validation.utils'
import { selectChartingInformationSnapshot } from '@utils/charting-information-snapshot.utils'
import { resolveChartProviderDisplayName } from '@utils/provider-options.util'

interface ChartingInformationFieldsProps {
  validationErrors: { [key: string]: string }
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >
  providerSearchList: { id: string; name: string }[]
  showExistingConditions: boolean
}

const ChartingInformationFields = ({
  validationErrors,
  setValidationErrors,
  providerSearchList,
  showExistingConditions,
}: ChartingInformationFieldsProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const info = useAppSelector(
    (state) => selectChartingInformationSnapshot(state, showExistingConditions),
    shallowEqual,
  )
  const providerOptions = useMemo(
    () =>
      providerSearchList.map((provider) => ({
        value: provider.id,
        label: provider.name,
      })),
    [providerSearchList],
  )
  const resolveProviderId = (nextValue: string): string => {
    const trimmed = nextValue.trim()
    if (!trimmed) {
      return nextValue
    }
    const byId = providerSearchList.find((provider) => provider.id === trimmed)
    if (byId) {
      return byId.id
    }
    const byName = providerSearchList.find(
      (provider) => provider.name.toLowerCase() === trimmed.toLowerCase(),
    )
    return byName?.id ?? nextValue
  }
  const providerComboboxValue = useMemo(() => {
    const match = providerSearchList.find(
      (provider) => provider.id === info.draftProviderId,
    )
    return match ? match.name : info.draftProviderId
  }, [info.draftProviderId, providerSearchList])
  const providerError =
    isChartProviderHardError(validationErrors.provider_id) &&
    !info.isPersistedChart

  const handleProviderChange = (nextValue: string): void => {
    if (info.isPersistedChart) {
      return
    }
    const nextProviderId = resolveProviderId(nextValue)
    const currentChart = store.getState().charting.createDentalChart
    const previousProviderId = currentChart.provider_id ?? ''
    if (nextProviderId === previousProviderId) {
      return
    }
    const isValidProvider = providerSearchList.some(
      (provider) => provider.id === nextProviderId,
    )
    dispatch(
      setCreateDentalChart({
        ...currentChart,
        provider_id: nextProviderId,
      }),
    )
    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      if (nextProviderId.trim() === '' || isValidProvider) {
        delete updatedErrors.provider_id
      } else {
        updatedErrors.provider_id = CHART_PROVIDER_INVALID_ERROR
      }
      return updatedErrors
    })
    dispatch(setDentalUpdated(true))
  }

  return (
    <div className="charting-info-fields">
      <InputField
        label="Patient name"
        variant="text"
        placeholder="Patient name"
        value={info.patientName}
        readOnly
        fullWidth
        clearable={false}
        error={!!validationErrors.patient_id && !info.hasPatientContext}
        helperText={
          validationErrors.patient_id && !info.hasPatientContext
            ? validationErrors.patient_id
            : undefined
        }
      />
      {info.isPersistedChart ? (
        <InputField
          label="Provider name"
          variant="text"
          placeholder="Provider name"
          value={resolveChartProviderDisplayName(
            info.persistedProvider,
            info.persistedProviderId,
            providerSearchList,
          )}
          readOnly
          fullWidth
          clearable={false}
        />
      ) : (
        <Combobox
          label="Provider name"
          placeholder="Provider name"
          options={providerOptions}
          value={providerComboboxValue}
          allowCustom
          filterable
          error={providerError}
          helperText={providerError ? validationErrors.provider_id : undefined}
          onChange={handleProviderChange}
        />
      )}
      {info.isPersistedChart ? (
        <InputField
          label="Chart name"
          variant="text"
          placeholder="Chart name"
          value={info.persistedTitle}
          readOnly
          fullWidth
          clearable={false}
        />
      ) : (
        <ChartNameComboBox
          value={info.draftTitle}
          readOnly={info.isPersistedChart}
          isInvalid={!!validationErrors.title && info.isNewChart}
          helperText={
            validationErrors.title && info.isNewChart
              ? validationErrors.title
              : undefined
          }
          onChange={(nextTitle): void => {
            const currentChart = store.getState().charting.createDentalChart
            dispatch(
              setCreateDentalChart({
                ...currentChart,
                title: nextTitle,
              }),
            )
            if (nextTitle.trim()) {
              setValidationErrors((prevErrors) => {
                const updatedErrors = { ...prevErrors }
                delete updatedErrors.title
                return updatedErrors
              })
            }
            dispatch(setDentalUpdated(true))
          }}
        />
      )}
    </div>
  )
}

export default React.memo(ChartingInformationFields)
